<script setup lang="ts">
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useQuery } from '@tanstack/vue-query';
import { useDashboardStats } from '@/composables/useDashboardStats';
import type { IManageOrganisation } from '@/types/manageOrgCommerce.types';
import type { IApiResponseOrder, Order } from '@/types/order.types';
import axiosInstance from '@/services/interceptor.services';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { orgs, isPending } = useDashboardStats();
const { t } = useI18n({ useScope: 'global' });

// ── Configuration client ──────────────────────────────────────────────────────
interface ConfigItem {
  field: string;
  icon: string;
  done: boolean;
}

function getChecklist(org: IManageOrganisation): ConfigItem[] {
  return [
    {
      field: 'Logo validé',
      icon: 'pi-image',
      done: !!org.logoUrl || !!org.logoId,
    },
    {
      field: 'Référent',
      icon: 'pi-user',
      done: org.contacts?.some((c) => c.isPrimary) ?? false,
    },
    {
      field: 'Téléphone',
      icon: 'pi-phone',
      done: org.contacts?.some((c) => c.isPrimary && !!c.phone) ?? false,
    },
    {
      field: 'Adresse',
      icon: 'pi-map-marker',
      done:
        org.addresses?.some((a) => a.isPrimary && !!a.addressLine1) ?? false,
    },
    { field: 'Commerce', icon: 'pi-shop', done: (org.storeCount ?? 0) > 0 },
    {
      field: 'E-commerce',
      icon: 'pi-wifi',
      done: org.stores?.some((s) => s.isActive) ?? false,
    },
  ];
}

function pct(list: ConfigItem[]) {
  return list.length
    ? Math.round((list.filter((i) => i.done).length / list.length) * 100)
    : 0;
}

const targets = computed(() =>
  orgs.value
    .filter((o) => getChecklist(o).some((i) => !i.done))
    .slice(0, 6)
    .map((o) => ({ org: o, list: getChecklist(o) })),
);

const activeTab = ref(0);
const current = computed(() => targets.value[activeTab.value] ?? null);
const avatarOrgs = computed(() => targets.value.slice(0, 3).map((t) => t.org));
const moreAvatars = computed(() => Math.max(0, targets.value.length - 3));

const globalPct = computed(() => {
  if (!targets.value.length) return 100;
  return Math.round(
    targets.value.reduce((a, t) => a + pct(t.list), 0) / targets.value.length,
  );
});

// ── Dernières commandes ───────────────────────────────────────────────────────
const allCommerces = computed(() =>
  orgs.value.flatMap((org) =>
    (org.stores ?? []).map((s) => ({
      restaurantId: s.id,
      commerceName: s.designation,
      clientName: org.name,
      isActive: s.isActive,
    })),
  ),
);

const { data: ordersData, isPending: ordersLoading } = useQuery({
  queryKey: computed(() => ['dash-orders-recent']),
  queryFn: async () => {
    const list = allCommerces.value.filter((c) => c.isActive).slice(0, 6);
    if (!list.length) return [];
    const results = await Promise.all(
      list.map((c) =>
        axiosInstance
          .get<IApiResponseOrder>(
            `orders/search?pagination=true&page=1&pageSize=6&sort=newer&restaurantId=${c.restaurantId}`,
          )
          .then((r) =>
            (r.data.data ?? []).map((o: Order) => ({
              ...o,
              _commerceName: c.commerceName,
            })),
          )
          .catch(() => [] as (Order & { _commerceName: string })[]),
      ),
    );
    return results
      .flat()
      .sort(
        (a, b) =>
          new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime(),
      )
      .slice(0, 6);
  },
  enabled: computed(() => !isPending.value && allCommerces.value.length > 0),
  staleTime: 60_000,
});

const recentOrders = computed(
  () => (ordersData.value ?? []) as (Order & { _commerceName: string })[],
);

function orderStatusClass(o: Order) {
  if (o.status === 'rejected' || o.status === 'cancelled') return 'error';
  if (o.status === 'ready') return 'ready';
  if (
    o.status === 'completed' ||
    o.status === 'served' ||
    o.status === 'terminated'
  )
    return 'success';
  return 'pending';
}

function orderStatusLabel(o: Order): string {
  const map: Record<string, string> = {
    pending: 'En attente',
    in_progress: 'En cours',
    ready: 'Prête',
    served: 'Servie',
    completed: 'Terminée',
    terminated: 'Terminée',
    cancelled: 'Annulée',
    rejected: 'Rejetée',
  };
  return map[o.status] ?? o.status;
}

function formatAmount(v: number, code: string) {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: code || 'EUR',
    maximumFractionDigits: 2,
  }).format(v);
}
</script>

<template>
  <!-- Configuration client — liquid-glass -->
  <div class="config-card">
    <div class="config-card__head">
      <h3 class="config-card__title">
        Configuration client
        <span v-if="!isPending" class="config-card__global-badge"
          >{{ globalPct }}% Global</span
        >
      </h3>
      <div class="config-card__avatars">
        <div
          v-for="org in avatarOrgs"
          :key="org.id"
          class="config-card__avatar"
          :title="org.name"
        >
          <img v-if="org.logoUrl" :src="org.logoUrl" :alt="org.name" />
          <span v-else>{{
            org.name
              .trim()
              .split(/\s+/)
              .slice(0, 2)
              .map((w: string) => w[0])
              .join('')
              .toUpperCase()
          }}</span>
        </div>
        <span v-if="moreAvatars > 0" class="config-card__avatar-more"
          >+{{ moreAvatars }} autres</span
        >
      </div>
    </div>

    <!-- Onglets stepper -->
    <div v-if="!isPending && targets.length > 0" class="config-card__tabs">
      <button
        v-for="(tab, i) in targets"
        :key="tab.org.id"
        class="config-card__tab"
        :class="{ 'config-card__tab--active': activeTab === i }"
        @click="activeTab = i"
      >
        {{ tab.org.name.split(' ')[0].toUpperCase() }}
        <span class="config-card__tab-pct">{{ pct(tab.list) }}%</span>
      </button>
    </div>

    <!-- Skeleton -->
    <div v-if="isPending" class="config-card__skeletons">
      <div v-for="i in 3" :key="i" class="config-card__skeleton" />
    </div>

    <!-- Contenu onglet actif -->
    <Transition name="tab-fade" mode="out-in">
      <div
        v-if="!isPending && current"
        :key="current.org.id"
        class="config-card__body"
      >
        <div class="config-card__progress-section">
          <div class="config-card__progress-header">
            <span class="config-card__progress-label">Étape actuelle</span>
            <span class="config-card__progress-count"
              >{{ current.list.filter((i) => i.done).length }} /
              {{ current.list.length }} validés</span
            >
          </div>
          <div class="config-card__progress-bar">
            <div
              class="config-card__progress-fill"
              :style="{ width: pct(current.list) + '%' }"
            />
          </div>
        </div>

        <div class="config-card__items">
          <div
            v-for="item in current.list"
            :key="item.field"
            class="config-card__item"
            :class="
              item.done ? 'config-card__item--done' : 'config-card__item--todo'
            "
          >
            <i :class="`pi ${item.done ? 'pi-check-circle' : 'pi-circle'}`" />
            <span>{{ item.field }}</span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Vide -->
    <div v-if="!isPending && targets.length === 0" class="config-card__empty">
      <i class="pi pi-check-circle" style="color: #16a34a; font-size: 1.5rem" />
      <p>Tous les clients sont configurés</p>
    </div>
  </div>

  <!-- Dernières commandes — white card -->
  <div class="orders-card">
    <div class="orders-card__head">
      <h3 class="orders-card__title">Dernières commandes</h3>
      <button class="orders-card__see-all" @click="router.push('/commandes')">
        Tout voir
      </button>
    </div>

    <div v-if="ordersLoading || isPending" class="orders-card__skeletons">
      <div v-for="i in 3" :key="i" class="orders-card__skeleton" />
    </div>

    <div v-else-if="recentOrders.length > 0" class="orders-card__table-wrap">
      <table class="orders-table">
        <thead>
          <tr>
            <th>Référence</th>
            <th>Montant</th>
            <th>Boutique</th>
            <th>Statut</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="o in recentOrders"
            :key="o.id"
            class="orders-table__row"
            @click="router.push('/commandes')"
          >
            <td class="orders-table__ref">#NX-{{ o.orderNumber }}</td>
            <td class="orders-table__amount">
              {{
                formatAmount(o.totalPrice, o.currencyOriginal?.label ?? 'EUR')
              }}
            </td>
            <td class="orders-table__store">
              {{ o._commerceName || o.restaurant?.designation }}
            </td>
            <td>
              <span
                class="orders-table__status"
                :class="`orders-table__status--${orderStatusClass(o)}`"
              >
                {{ orderStatusLabel(o) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="orders-card__empty">
      <i class="pi pi-inbox" style="font-size: 1.5rem; opacity: 0.3" />
      <p>Aucune commande récente</p>
    </div>
  </div>
</template>

<style scoped>
/* ── Configuration client — liquid-glass ── */
.config-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 8px 32px rgba(11, 19, 32, 0.03);
  border-radius: 2.5rem;
  padding: 2rem;
  box-sizing: border-box;
}

.config-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.5rem;
  gap: 1rem;
  flex-wrap: wrap;
}
.config-card__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0b1320;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  flex-wrap: wrap;
}
.config-card__global-badge {
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.175rem 0.5rem;
  border-radius: 9999px;
  background: rgba(255, 214, 10, 0.2);
  color: #0b1320;
  letter-spacing: 0.04em;
}

.config-card__avatars {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-shrink: 0;
}
.config-card__avatar {
  width: 2rem;
  height: 2rem;
  min-width: 2rem;
  border-radius: 50%;
  background: #0b1320;
  color: #fff;
  font-size: 0.5625rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #fff;
  margin-left: -0.5rem;
  overflow: hidden;
  flex-shrink: 0;
}
.config-card__avatar:first-child {
  margin-left: 0;
}
.config-card__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.config-card__avatar-more {
  font-size: 0.6875rem;
  font-weight: 700;
  color: rgba(11, 19, 32, 0.4);
  margin-left: 0.375rem;
  white-space: nowrap;
}

/* Tabs */
.config-card__tabs {
  display: flex;
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
  margin-bottom: 2rem;
  overflow-x: auto;
  scrollbar-width: none;
}
.config-card__tabs::-webkit-scrollbar {
  display: none;
}

.config-card__tab {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition:
    color 0.15s,
    border-color 0.15s;
  margin-bottom: -1px;
}
.config-card__tab:hover {
  color: #0b1320;
}
.config-card__tab--active {
  color: #0b1320;
  border-bottom-color: #ffd60a;
}
.config-card__tab-pct {
  font-size: 0.6875rem;
  color: rgba(11, 19, 32, 0.4);
  font-weight: 500;
}
.config-card__tab--active .config-card__tab-pct {
  color: rgba(11, 19, 32, 0.55);
}

/* Skeleton */
.config-card__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.config-card__skeleton {
  height: 3rem;
  border-radius: 0.625rem;
  background: linear-gradient(
    90deg,
    rgba(11, 19, 32, 0.04) 25%,
    rgba(11, 19, 32, 0.07) 50%,
    rgba(11, 19, 32, 0.04) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Body */
.config-card__body {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}
@media (min-width: 640px) {
  .config-card__body {
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
  }
}

.config-card__progress-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.config-card__progress-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
.config-card__progress-label {
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(11, 19, 32, 0.4);
}
.config-card__progress-count {
  font-size: 0.875rem;
  font-weight: 700;
  color: #0b1320;
}
.config-card__progress-bar {
  height: 8px;
  border-radius: 9999px;
  background: rgba(11, 19, 32, 0.05);
  overflow: hidden;
}
.config-card__progress-fill {
  height: 100%;
  border-radius: 9999px;
  background: #ffd60a;
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.config-card__items {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-content: start;
}
.config-card__item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
}
.config-card__item--done {
  color: #16a34a;
}
.config-card__item--done .pi {
  color: #16a34a;
}
.config-card__item--todo {
  color: rgba(11, 19, 32, 0.2);
}
.config-card__item--todo .pi {
  color: rgba(11, 19, 32, 0.2);
}

.config-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: rgba(11, 19, 32, 0.35);
  text-align: center;
  font-size: 0.875rem;
}
.config-card__empty p {
  margin: 0;
}

/* Transition */
.tab-fade-enter-active,
.tab-fade-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.tab-fade-enter-from {
  opacity: 0;
  transform: translateX(8px);
}
.tab-fade-leave-to {
  opacity: 0;
  transform: translateX(-8px);
}

/* ── Dernières commandes — white card ── */
.orders-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 2.5rem;
  padding: 2rem;
  box-sizing: border-box;
}

.orders-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 1rem;
}
.orders-card__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: #0b1320;
}
.orders-card__see-all {
  background: none;
  border: none;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(11, 19, 32, 0.4);
  cursor: pointer;
  font-family: inherit;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 0.15s;
}
.orders-card__see-all:hover {
  color: #0b1320;
}

.orders-card__skeletons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.orders-card__skeleton {
  height: 3.5rem;
  border-radius: 0.625rem;
  background: linear-gradient(90deg, #f0f2f5 25%, #e8eaed 50%, #f0f2f5 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
}

.orders-card__table-wrap {
  overflow-x: auto;
}
.orders-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}
.orders-table thead tr {
  text-align: left;
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
}
.orders-table th {
  padding-bottom: 1rem;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: rgba(11, 19, 32, 0.3);
}
.orders-table__row {
  border-bottom: 1px solid rgba(11, 19, 32, 0.05);
  cursor: pointer;
  transition: background 0.12s;
}
.orders-table__row:last-child {
  border-bottom: none;
}
.orders-table__row:hover {
  background: rgba(11, 19, 32, 0.01);
}
.orders-table td {
  padding: 1.25rem 0;
  vertical-align: middle;
}
.orders-table td:not(:last-child) {
  padding-right: 1rem;
}

.orders-table__ref {
  font-weight: 700;
  font-size: 0.875rem;
  color: #0b1320;
}
.orders-table__amount {
  font-weight: 700;
  font-size: 0.875rem;
  color: #0b1320;
}
.orders-table__store {
  font-size: 0.875rem;
  color: rgba(11, 19, 32, 0.6);
}

.orders-table__status {
  display: inline-block;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}
.orders-table__status--pending {
  background: rgba(11, 19, 32, 0.05);
  color: rgba(11, 19, 32, 0.4);
}
.orders-table__status--success {
  background: rgba(255, 214, 10, 0.2);
  color: #0b1320;
}
.orders-table__status--ready {
  background: rgba(255, 214, 10, 0.2);
  color: #0b1320;
}
.orders-table__status--error {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
}

.orders-card__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  color: rgba(11, 19, 32, 0.35);
  text-align: center;
  font-size: 0.875rem;
}
.orders-card__empty p {
  margin: 0;
}
</style>

<style>
[data-theme='dark'] .config-card {
  background: rgba(20, 28, 41, 0.75);
  border-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
[data-theme='dark'] .config-card__title {
  color: #f1f5f9;
}
[data-theme='dark'] .config-card__global-badge {
  background: rgba(255, 214, 10, 0.15);
  color: #ffd60a;
}
[data-theme='dark'] .config-card__tabs {
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .config-card__tab {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .config-card__tab:hover,
[data-theme='dark'] .config-card__tab--active {
  color: #f1f5f9;
}
[data-theme='dark'] .config-card__progress-bar {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .config-card__progress-label {
  color: rgba(255, 255, 255, 0.35);
}
[data-theme='dark'] .config-card__progress-count {
  color: #f1f5f9;
}
[data-theme='dark'] .config-card__item--todo {
  color: rgba(255, 255, 255, 0.2);
}
[data-theme='dark'] .config-card__item--todo .pi {
  color: rgba(255, 255, 255, 0.2);
}
[data-theme='dark'] .config-card__skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
}

[data-theme='dark'] .orders-card {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .orders-card__title {
  color: #f1f5f9;
}
[data-theme='dark'] .orders-card__see-all {
  color: rgba(255, 255, 255, 0.3);
}
[data-theme='dark'] .orders-card__see-all:hover {
  color: #f1f5f9;
}
[data-theme='dark'] .orders-table__row {
  border-color: rgba(255, 255, 255, 0.06);
}
[data-theme='dark'] .orders-table__row:hover {
  background: rgba(255, 255, 255, 0.03);
}
[data-theme='dark'] .orders-table th {
  color: rgba(255, 255, 255, 0.25);
  border-color: rgba(255, 255, 255, 0.07);
}
[data-theme='dark'] .orders-table__ref,
[data-theme='dark'] .orders-table__amount {
  color: #f1f5f9;
}
[data-theme='dark'] .orders-table__store {
  color: rgba(255, 255, 255, 0.4);
}
[data-theme='dark'] .orders-card__skeleton {
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.07) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
}
</style>
